import { type CustomCellRendererProps } from '@mint-lib/ag-grid/react';
import type { FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ChevronDown } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { Chip } from '@mint-lib/tw-components';

import {
  getMapChipMetaByFlowStatus,
  READONLY_FLOW_STATUSES,
  type FlowStatuses,
} from '../../../constants/singleFlow.js';
import type { ApprovalTableItemData } from '../../../types/approversTable.js';

type Props = CustomCellRendererProps<ApprovalTableItemData, FlowStatuses>;

const StatusCell: FC<Props> = ({ api, node, column, value }) => {
  const { t } = useTranslation('@myn/approval_v2');
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');

  const chipMetaMapByFlowStatus = getMapChipMetaByFlowStatus(t);
  const chipMeta = value ? chipMetaMapByFlowStatus[value] : null;
  const canModifyStatus = value
    ? !READONLY_FLOW_STATUSES.includes(value)
    : false;
  const showChevronIcon = !isReadOnly && canModifyStatus;

  const handleClick = () => {
    const colKey = column?.getColId();
    const rowIndex = node.rowIndex;

    if (
      isReadOnly ||
      !canModifyStatus ||
      !colKey ||
      typeof rowIndex !== 'number'
    ) {
      return;
    }

    api.startEditingCell({
      rowIndex,
      colKey,
    });
  };

  if (!chipMeta) {
    return null;
  }

  return (
    <span className="flex h-full w-full border-x border-transparent py-1">
      <Chip onClick={handleClick} {...chipMeta}>
        {chipMeta.label}
        {showChevronIcon && <ChevronDown />}
      </Chip>
    </span>
  );
};

export default StatusCell;
